import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import type { DateRangePickerValue } from '@roger/ui-components';
import {
  exportAdoptionReportToCSV,
  exportAdoptionReportToXLSX,
} from '../../utils/adoptionReportExport';
import { getAdoptionReport } from '../../services/rogerDocApi';
import {
  type AdoptionReportEntry,
  toAdoptionReportEntry,
} from '../../types/adoptionReport.types';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import VisitCompletionByClinicianReportHeader from './VisitCompletionByClinicianReportHeader';
import VisitCompletionByClinicianReportTable from './VisitCompletionByClinicianReportTable';
import { AdoptionReportExportFormatDialog } from './AdoptionReportExportFormatDialog';
import { useClinicianPhoneLookup } from '../../hooks/useClinicianPhoneLookup';

export interface VisitCompletionByClinicianRef {
  exportToCSV: () => void;
}

export interface VisitCompletionByClinicianProps {
  onExportRegistry?: (exportFunc: (() => void) | null) => void;
}

const VisitCompletionByClinician = forwardRef<
  VisitCompletionByClinicianRef,
  VisitCompletionByClinicianProps
>(({ onExportRegistry }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState<AdoptionReportEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(() => {
    const saved = localStorage.getItem('adoptionReport_showBreakdown');
    return saved ? JSON.parse(saved) : false;
  });
  const [hideNeverLoggedIn, setHideNeverLoggedIn] = useState(() => {
    const saved = localStorage.getItem('adoptionReport_hideNeverLoggedIn');
    return saved ? JSON.parse(saved) : false;
  });
  const [dateRange, setDateRange] = useState<DateRangePickerValue>({});
  const [exportFormatDialogOpen, setExportFormatDialogOpen] = useState(false);

  const requestIdRef = useRef(0);

  const { phonesByUsername, status: phoneLookupStatus } =
    useClinicianPhoneLookup();
  // Only a settled lookup can back a phone column: while it loads, or after
  // it failed, an empty map would render and export a column of blanks.
  const phoneLookup =
    phoneLookupStatus === 'ready' ? phonesByUsername : undefined;

  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    setReportData([]);

    const fetchReportData = async () => {
      try {
        const data = await getAdoptionReport(dateRange.from, dateRange.to);
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        const processedData = data.map(rawData =>
          toAdoptionReportEntry(rawData)
        );
        setReportData(processedData);
        showSuccess(`Loaded ${processedData.length} clinician records`);
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        console.error('Error loading adoption report:', err);
        setError('Failed to load report data. Please try again.');
        showError(
          'Report Failed',
          'Failed to load report data. Please try again.'
        );
        setReportData([]);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void fetchReportData();
    // showSuccess/showError are stable Zustand actions; omit from deps to avoid extra refetches
  }, [dateRange.from, dateRange.to]);

  const getFilenameBase = () =>
    `roger_adoption_report_${new Date().toISOString().split('T')[0]}`;

  const exportAsCSV = () => {
    exportAdoptionReportToCSV(reportData, getFilenameBase(), false, {
      phonesByUsername: phoneLookup,
    });
  };

  const exportAsXLSX = async () => {
    try {
      await exportAdoptionReportToXLSX(
        reportData,
        getFilenameBase(),
        false,
        'Adoption Report',
        { phonesByUsername: phoneLookup }
      );
    } catch (err) {
      console.error('Error exporting adoption report to XLSX:', err);
      showError('Failed to export report. Please try again.');
    }
  };

  const openExportDialog = useCallback(() => {
    setExportFormatDialogOpen(true);
  }, []);

  const handleExportConfirm = (format: 'csv' | 'xlsx') => {
    if (format === 'xlsx') {
      void exportAsXLSX();
    } else {
      exportAsCSV();
    }
  };

  useImperativeHandle(ref, () => ({
    exportToCSV: openExportDialog,
  }));

  useEffect(() => {
    if (onExportRegistry) {
      if (loading || reportData.length === 0) {
        onExportRegistry(null);
      } else {
        onExportRegistry(openExportDialog);
      }
    }
  }, [loading, reportData.length, onExportRegistry, openExportDialog]);

  return (
    <>
      <AdoptionReportExportFormatDialog
        open={exportFormatDialogOpen}
        onOpenChange={setExportFormatDialogOpen}
        onConfirm={handleExportConfirm}
      />

      <VisitCompletionByClinicianReportHeader
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        showBreakdown={showBreakdown}
        onShowBreakdownChange={setShowBreakdown}
        hideNeverLoggedIn={hideNeverLoggedIn}
        onHideNeverLoggedInChange={setHideNeverLoggedIn}
      />

      <VisitCompletionByClinicianReportTable
        data={reportData}
        loading={loading}
        error={error}
        showBreakdown={showBreakdown}
        hideNeverLoggedIn={hideNeverLoggedIn}
        searchTerm={searchTerm}
        phonesByUsername={phoneLookup}
      />
    </>
  );
});

VisitCompletionByClinician.displayName = 'VisitCompletionByClinician';

export default VisitCompletionByClinician;
